import React from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import {
  FaMapMarkerAlt,
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaTiktok,
} from 'react-icons/fa'
import CmsImage from './CmsImage'
import { usePageLink } from '../../hooks/usePageLink'
import { useLocalizedNodes } from '../../hooks/useLocalizedNodes'
import { useLanguage } from '../../context/LanguageContext'
import { NOM_NOM_SLUG, getHomePath } from '../../utils/paths'

const NAV_SLUGS = {
  EL: ['home', 'our_products', 'company', 'news', 'contact'],
  EN: ['homepage', 'products', 'our-team', 'blog', 'contactus'],
}
const HOME_SLUGS = ['home', 'homepage']

const THEMES = {
  default: {
    footer: 'bg-night bg-footer-pattern bg-no-repeat bg-left bg-contain',
    logoBox: 'bg-brand',
    heading: 'text-paper',
    item: 'text-silver',
    contactIcon: 'text-brand',
    socialIcons: 'text-night',
    socialLink: 'bg-brand',
  },
  products: {
    footer: 'bg-nomnom-teal',
    logoBox: 'bg-nomnom-ink',
    heading: 'text-[#121111]',
    item: 'text-nomnom-ink',
    contactIcon: 'text-nomnom-ink',
    socialIcons: 'text-nomnom-teal',
    socialLink: 'bg-nomnom-ink',
  },
}

const FooterHeading = ({ className, children }) => (
  <h2 className={`${className} text-[16px] leading-[100%] pb-4 font-display font-normal`}>
    {children}
  </h2>
)

const Footer = ({ variant = 'default' }) => {
  const { lang } = useLanguage()
  const theme = THEMES[variant]
  const cookiesPolicyPage = usePageLink('cookies-policy')
  const privacyPolicyPage = usePageLink('privacy-policy')
  // Nom-Nom is currently the only product, so every product entry links to its page.
  const productPage = usePageLink(NOM_NOM_SLUG)

  const data = useStaticQuery(graphql`
    query {
      site {
        buildTime(formatString: "YYYY")
      }

      footerPages: allWpPage(filter: { slug: { in: ["footer", "footer-en"] } }) {
        nodes {
          slug
          footerFields {
            footerLabel
            footerDescription
            footerLogo {
              ...CmsImageField
            }
            navigationLabel
            productsLabel
            officesLabel
            socialLabel
            facebook
            instagram
            linkedin
            tiktok
            location
            email
            additionalEmail
          }
        }
      }

      allWpPage {
        nodes {
          id
          slug
          uri
          title
        }
      }

      allWpProduct {
        nodes {
          id
          language {
            code
          }
          productsFields {
            productName
          }
        }
      }
    }
  `)

  const footerSlug = lang === 'EN' ? 'footer-en' : 'footer'
  const {
    footerLabel,
    footerLogo,
    footerDescription,
    navigationLabel,
    productsLabel,
    socialLabel,
    facebook,
    instagram,
    linkedin,
    tiktok,
    officesLabel,
    location,
    email,
    additionalEmail,
  } = data.footerPages.nodes.find((page) => page.slug === footerSlug).footerFields

  const navItems = data.allWpPage.nodes.filter((page) => NAV_SLUGS[lang]?.includes(page.slug))
  const products = useLocalizedNodes(data.allWpProduct.nodes)

  const socialLinks = [
    { label: 'Facebook', icon: FaFacebookF, url: facebook },
    { label: 'Instagram', icon: FaInstagram, url: instagram },
    { label: 'LinkedIn', icon: FaLinkedinIn, url: linkedin },
    { label: 'TikTok', icon: FaTiktok, url: tiktok },
  ]

  return (
    <footer className={`relative z-10 ${theme.footer} pt-16 pb-12 rounded-t-[50px] -mt-12`}>
      {/* Decorative circles, bottom right */}
      <div className="absolute bottom-0 right-0 z-0 opacity-10 pointer-events-none">
        <div className="w-[169px] h-[169px] border border-silver rounded-full absolute bottom-12 right-12" />
        <div className="w-[125px] h-[125px] border border-silver rounded-full absolute bottom-24 right-24" />
        <div className="w-[93px] h-[93px] border border-silver rounded-full absolute bottom-36 right-36" />
      </div>
      <div className="container-sm mx-auto px-6 -mt-8 sm:mt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-8 mb-12">
          {/* Logo and description */}
          <div
            className={`col-span-full lg:col-span-2 order-1 lg:order-none ${theme.logoBox} p-6 rounded-[30px]`}
          >
            <CmsImage image={footerLogo} alt="xPantia" className="mb-4" />
            <span className="text-paper text-[20px] font-bold font-display">{footerLabel}</span>
            <div className="w-full h-px bg-silver mb-4" />
            <p className="text-paper leading-[120%]">{footerDescription}</p>
          </div>

          {/* Navigation & products: side by side below lg, separate grid columns from lg */}
          <div className="col-span-full order-2 lg:order-none grid grid-cols-2 gap-8 lg:contents">
            <div className="col-span-1 mt-12">
              <FooterHeading className={theme.heading}>{navigationLabel}</FooterHeading>
              <ul className={`font-display space-y-2 text-[16px] ${theme.item}`}>
                {navItems.map((item) => (
                  <li key={item.id}>
                    /{' '}
                    <Link
                      to={HOME_SLUGS.includes(item.slug) ? getHomePath(lang) : item.uri}
                      className="hover:underline"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-span-1 mt-12">
              <FooterHeading className={theme.heading}>{productsLabel}</FooterHeading>
              <ul className={`font-display space-y-2 text-[16px] ${theme.item}`}>
                {products.map((product) => (
                  <li key={product.id}>
                    /{' '}
                    <Link to={productPage.uri} className="hover:underline">
                      {product.productsFields.productName}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Offices */}
          <div className="col-span-full lg:col-span-2 order-3 lg:order-none mt-12 flex flex-col items-center text-center lg:items-start lg:text-left">
            <FooterHeading className={theme.heading}>{officesLabel}</FooterHeading>
            <ul className="font-display space-y-3 text-[16px] text-silver">
              <li className="flex items-start gap-2 justify-center lg:justify-start">
                <FaMapMarkerAlt className={`${theme.contactIcon} mt-1 text-2xl`} />
                <span className={theme.item}>{location}</span>
              </li>
              <li className="flex items-start gap-2 justify-center lg:justify-start">
                <FaEnvelope className={`${theme.contactIcon} mt-1 text-2xl`} />
                <div className="flex flex-col">
                  <a href={`mailto:${email}`} className={`hover:underline ${theme.item}`}>
                    {email}
                  </a>
                  {additionalEmail && (
                    <a href={`mailto:${additionalEmail}`} className="hover:underline">
                      {additionalEmail}
                    </a>
                  )}
                </div>
              </li>
            </ul>
          </div>

          {/* Social media */}
          <div className="col-span-full lg:col-span-1 order-4 lg:order-none mt-12 flex flex-col items-center text-center lg:items-start lg:text-left">
            <FooterHeading className={theme.heading}>{socialLabel}</FooterHeading>
            {/* Two by two from lg, where four in a row are wider than this grid column */}
            <div className={`flex gap-4 lg:grid lg:grid-cols-2 ${theme.socialIcons} text-lg`}>
              {socialLinks.map(({ label, icon: Icon, url }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`w-8 h-8 rounded-full ${theme.socialLink} flex items-center justify-center hover:opacity-80 hover:scale-105`}
                >
                  <Icon size={24} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Copyright + policy links */}
        <div
          className={`border-t border-muted pt-6 flex flex-col lg:flex-row justify-between items-center text-[16px] font-sans gap-2 ${theme.item}`}
        >
          <p className={theme.item}>
            Copyright ©xPantia {data.site.buildTime}. All rights reserved.
          </p>
          <div className="flex gap-4">
            <Link to={privacyPolicyPage.uri} className="hover:underline">
              {privacyPolicyPage.title}
            </Link>
            <Link to={cookiesPolicyPage.uri} className="hover:underline">
              {cookiesPolicyPage.title}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
